<template>
    <div class="animated fadeIn">
        <div
            v-if="canCreate"
            class="kgr-row kgr-text--rightAligned"
        >
            <a
                class="kgr-button kgr-button--accent"
                :href="createPath"
            >
                {{ $t('Neuen Beitrag anlegen') }}
            </a>
        </div>
        <div
            class="kgr-row"
        >
            <kgr-text-input
                v-model="filterQuery"
                inline
                :label="$t('Suchen')"
            />
            <label
                class="kgr-inputGroup kgr-inputGroup--inline"
            ><div
                 class="kgr-inputGroup__label ui-droppable"
             >
                 {{ $t('sort') }}
             </div>

                <kgr-generic-select
                    v-model="sort"
                    :options="sortOptions"
                    display="text"
                    no-null-option
                    inline
                />
            </label>
            <kgr-checkbox
                v-model="filter.showArchived"
                :label="$t('Archivierte Beiträge anzeigen')"
                inline
            />
        </div>
        <div class="kgr-alert kgr-alert--info kgr-row kgr-text">
            {{ $t('Bitte beachte, dass nur Beiträge gelöscht werden können, die keinem Mitgliedsbeitrag zugeordnet sind.') }}
        </div>
        <table class="kgr-table kgr-table--block kgr-table--striped printable">
            <thead>
                <tr class="kgr-table__row">
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Aktiv') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Beitrag') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Kategorie') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Abteilung') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Preis') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('lädt Guthaben auf') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('zu zahlen') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Zahlungsintervall') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline kgr-table__cell--actions kgr-hidden-print"></th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="loading">
                    <td
                        colspan="9"
                        style="text-align: center;"
                    >
                        <kgr-loader />
                    </td>
                </tr>
                <tr v-else-if="fees.length === 0">
                    <td
                        colspan="9"
                        style="text-align: center;"
                    >
                        {{ $t('dataTable.noData') }}
                    </td>
                </tr>
                <template v-else>
                    <tr
                        v-for="fee in filteredFees"
                        :key="fee.id"
                        class="kgr-table__row"
                        :class="{ 'is-inactive': fee.isArchived }"
                    >
                        <td class="kgr-table__cell">
                            <kgr-icon
                                disabled
                                :icon-class="fee.isArchived ? 'clear' : 'check'"
                            />
                        </td>
                        <td class="kgr-table__cell">
                            {{ fee.name }}
                            <kgr-icon
                                v-if="fee.note !== ''"
                                :title="`Für diesen Beitrag gibt es einen zusätzlichen Rechnungshinweis: ${fee.note}`"
                                icon-class="info-circle"
                                small
                                light
                                disabled
                            />
                        </td>
                        <td class="kgr-table__cell">
                            <template v-if="!fee.category">
                                -
                            </template>
                            <template v-else>
                                {{ fee.category.name }}
                            </template>
                        </td>
                        <td class="kgr-table__cell">
                            <template v-if="!fee.department">
                                -
                            </template>
                            <template v-else>
                                {{ fee.department.name }}
                            </template>
                        </td>
                        <td class="kgr-table__cell">
                            {{ getPrice(fee) }}
                            <button
                                v-if="fee.canEdit"
                                class="kgr-table__action"
                                @click="openPriceDialog(fee)"
                            >
                                <kgr-icon
                                    icon-class="edit"
                                    small
                                />
                            </button>
                            <template v-if="getUpcomingPriceChange(fee)">
                                <kgr-icon
                                    small
                                    :title="upcomingPriceChangeInfo(fee)"
                                    disabled
                                    icon-class="info-circle"
                                />
                            </template>
                        </td>
                        <td class="kgr-table__cell">
                            <kgr-icon
                                v-if="fee.chargeCredit"
                                icon-class="check"
                                disabled
                                small
                            />
                        </td>
                        <td class="kgr-table__cell">
                            {{ helper.translatePaymentApproach(fee.paymentApproach) }}
                        </td>
                        <td class="kgr-table__cell">
                            {{ fee.paymentIntervalSortableString }}
                        </td>
                        <td class="kgr-table__cell kgr-table__cell--actions kgr-hidden-print">
                            <button
                                v-if="fee.canEdit"
                                class="kgr-table__action"
                                @click="editClubFee(fee)"
                            >
                                <kgr-icon icon-class="edit" />
                            </button>
                            <button
                                v-if="fee.canDelete"
                                class="kgr-table__action"
                                :disabled="!fee.isDeletable"
                                @click="deleteFee(fee.id)"
                            >
                                <kgr-icon
                                    icon-class="delete"
                                    :class="{ 'kgr-icon--light noPointer': !fee.isDeletable }"
                                />
                            </button>
                            <button
                                v-if="fee.canEdit"
                                class="kgr-table__action"
                                @click="toggleArchived(fee)"
                            >
                                <kgr-icon
                                    :icon-class="fee.isArchived ? 'unarchive' : 'archive'"
                                />
                            </button>
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>
        <kgr-price-history-dialog
            v-if="modalFee && showPriceDialog"
            :fee="modalFee"
            :show="showPriceDialog"
            @close="closePriceDialog"
            @updated="closePriceDialog();loadFees()"
        />
    </div>
</template>

<script>
import Checkbox from '../../../components/common/form/checkbox/Checkbox.vue';
import ClubFeeRepository from '../../../js/repository/ClubFeeRepository.js';
import Error from '../../../js/model/Error.js';
import GenericSelect from '../../../components/common/form/select/GenericSelect.vue';
import Helper from '../../../js/accounting/Helper.js';
import Icon from '../../../components/common/icon/Icon.vue';
import Loading from '../../../components/common/Loading.vue';
import PriceHistory from '../../../js/dto/PriceHistory.js';
import PriceHistoryDialog from '../../../components/club/accounting/PriceHistoryDialog.vue';
import TextInput from '../../../components/common/form/text-input/TextInput.vue';

export default {
    components: {
        'kgr-checkbox': Checkbox,
        'kgr-icon': Icon,
        'kgr-loader': Loading,
        'kgr-text-input': TextInput,
        'kgr-generic-select': GenericSelect,
        'kgr-price-history-dialog': PriceHistoryDialog,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        canCreate: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            sort: null,
            sortOptions: [
                { value: 'name', text: this.$t('Beitrag') },
                { value: 'category', text: this.$t('Kategorie') },
                { value: 'department', text: this.$t('Abteilung') },
                { value: 'paymentInterval', text: this.$t('Zahlungsintervall') },
                { value: 'paymentApproach', text: this.$t('zu zahlen') },
            ],
            error: new Error(),
            helper: new Helper(),
            showPriceDialog: false,
            modalFee: null,
            loading: false,
            filterQuery: '',
            impersonalAccounts: [],
            fees: [],
            filter: {
                showArchived: false,
            },
        };
    },
    computed: {
        createPath() {
            return `/backend/clubs/${this.clubId}/accounting/fee-management/new`;
        },
        filteredFees() {
            let fees = this.fees;
            if (!this.filter.showArchived) {
                fees = fees.filter((fee) => !fee.isArchived);
            }
            if (this.filterQuery.trim() !== '') {
                const query = this.filterQuery.trim().toLowerCase();
                fees = fees.filter((fee) => {
                    const nameMatch = fee.name?.toLowerCase().includes(query);
                    const categoryMatch = fee.category?.name?.toLowerCase().includes(query);
                    const departmentMatch = fee.department?.name?.toLowerCase().includes(query);
                    const paymentIntervalMatch = fee.paymentIntervalSortableString?.toLowerCase().includes(query);

                    return nameMatch || categoryMatch || departmentMatch || paymentIntervalMatch;
                });
            }
            fees = fees.slice();

            fees.sort((a, b) => {
                let aValue, bValue;

                switch (this.sort?.value) {
                    case 'name':
                        aValue = a.name || '';
                        bValue = b.name || '';
                        break;
                    case 'category':
                        aValue = a.category?.name || '';
                        bValue = b.category?.name || '';
                        break;
                    case 'department':
                        aValue = a.department?.name || '';
                        bValue = b.department?.name || '';
                        break;
                    case 'paymentInterval':
                        aValue = a.paymentIntervalSortableString || '';
                        bValue = b.paymentIntervalSortableString || '';
                        break;
                    case 'paymentApproach':
                        aValue = b.paymentApproach || '';
                        bValue = a.paymentApproach || '';
                        break;
                    default:
                        aValue = '';
                        bValue = '';
                }

                return aValue.localeCompare(bValue);
            });

            return fees;
        },
    },
    created() {
        this.loadFees();
        this.sort = this.sortOptions[0];
    },
    methods: {
        getPrice(fee) {
            const currentPricePerTime = fee.priceHistory.getCurrentPricePerTime();
            return this.helper.getPricePerTime(currentPricePerTime);
        },
        loadFees() {
            this.loading = true;
            ClubFeeRepository.findByClubForAccounting(this.clubId).then((response) => {
                this.fees = response;
                this.fees.forEach((fee) => {
                    fee.paymentIntervalSortableString = this.helper.getPaymentInterval(fee); // workaround v-data-table sort issue
                });
                this.loading = false;
            });
        },
        deleteFee(clubFeeId) {
            showConfirm(this.$t('confirmInformal'), this.$t('Willst du den Beitrag wirklich löschen?'), () => {
                this.loading = true;
                ClubFeeRepository.delete(clubFeeId)
                    .then(() => this.loadFees())
                    .catch((error) => this.$notify.error(error.status === 400 ? 'club.accounting.clubFee.delete.error' : 'saveChanges.error'))
                    .finally(() => this.loading = false);
            }, this.$t('delete'));
        },
        toggleArchived(fee) {
            fee.isArchived = !fee.isArchived;
            ClubFeeRepository.archive(fee)
                .then(() => this.loadFees())
                .catch(() => this.loading = false);
        },
        getUpcomingPriceChange(fee) {
            return new PriceHistory(fee.basePrice, fee.priceHistory.priceChanges).getUpcomingPriceChange();
        },
        upcomingPriceChangeInfo(fee) {
            const priceChange = fee.priceHistory.getUpcomingPriceChange();
            return `Am ${new Helper().formatDate(priceChange.start)} findet eine Preisänderung statt. Der neue Preis lautet ${new Helper().getPricePerTime(priceChange.pricePerTime)}`;
        },
        setModalFee(clubFee) {
            this.modalFee = clubFee;
        },
        openPriceDialog(clubFee) {
            this.setModalFee(clubFee);
            this.showPriceDialog = true;
        },
        closePriceDialog() {
            this.modalFee = null;
            this.showPriceDialog = false;
        },
        editClubFee(clubFee) {
            window.location = `/backend/clubs/${this.clubId}/accounting/fee-management/${clubFee.id}/edit`;
        },
    },
};
</script>

<style scoped lang="scss">
.noPointer {
    pointer-events: none;
}
</style>
